import { Pagination } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar,
  Edit,
  Eye,
  Heart,
  MoreVertical,
  Music,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MegaLoader from "../../components/MegaLoader";
import { Endpoints } from "../../lib/endpoints";
import { PaginatedResponse } from "../../lib/responses";
import { usePerformRequest } from "../../lib/usePerformRequest";
import { IHymnProps } from "../../types";

const HymnsPage: React.FC = () => {
  const [activeActionId, setActiveActionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, 500);
  const [page, setPage] = useState(1);
  const limit = 10;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: hymnsData, isLoading } = usePerformRequest<
    PaginatedResponse<Omit<IHymnProps, "verses">[]>
  >({
    method: Endpoints.GetHymns(page, limit, debouncedSearch).method,
    route: Endpoints.GetHymns(page, limit, debouncedSearch).route,
  });

  const hymns = hymnsData?.data || [];
  const totalRecords = hymnsData?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / limit);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveActionId(null);
      }
    };

    if (activeActionId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeActionId]);

  const handleDeleteHymn = async (hymnId: string) => {
    if (window.confirm("Are you sure you want to delete this hymn?")) {
      try {
        console.log("Deleting hymn:", hymnId);
        // await deleteHymn(hymnId);
        // reloadData();
      } catch (error) {
        console.error("Error deleting hymn:", error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-900 to-black pt-12 pb-8 px-6 lg:px-12 border-b border-gray-800">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center justify-center p-3 bg-blue-500/10 rounded-2xl mb-4"
          >
            <Music className="w-8 h-8 text-blue-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400"
          >
            My Favourite Hymns
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-xl mx-auto"
          >
            Manage and curate your collection of timeless hymns. Create, edit,
            and organize them effortlessly.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="pt-6 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/d/create-hymn"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl font-medium shadow-lg hover:shadow-blue-500/25 transition-all duration-200"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Hymn
            </Link>

            {/* Search Bar */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search hymns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-gray-200 placeholder-gray-500 transition-all outline-none"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {isLoading ? (
          <MegaLoader />
        ) : hymns.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <Music className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <p className="text-xl font-medium">No hymns found</p>
            <p className="text-sm">
              Try adjusting your search or create a new one.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {hymns.map((hymn: any, index: number) => (
                  <motion.div
                    key={hymn.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                    className="group relative bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 hover:shadow-2xl transition-all duration-300"
                  >
                    {/* Decoration Gradient */}
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 opacity-50 group-hover:opacity-100 transition-opacity" />

                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono font-medium bg-gray-800 text-gray-400 border border-gray-700">
                          <Calendar className="w-3 h-3 mr-1.5" />
                          {hymn.pub.year}
                        </span>

                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveActionId(
                                activeActionId === hymn.id ? null : hymn.id,
                              );
                            }}
                            className="p-2 text-gray-500 hover:text-white bg-transparent hover:bg-gray-800 rounded-lg transition-colors"
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>

                          {/* Dropdown Menu */}
                          {activeActionId === hymn.id && (
                            <div
                              ref={dropdownRef}
                              className="absolute right-0 top-full mt-2 w-48 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden"
                            >
                              <div className="p-1">
                                <Link
                                  to={`/d/hymn/${hymn.id}`}
                                  className="flex items-center w-full px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                                  onClick={() => setActiveActionId(null)}
                                >
                                  <Eye className="w-4 h-4 mr-2" /> View Details
                                </Link>
                                <Link
                                  to={`/d/hymns/${hymn.id}/edit`}
                                  className="flex items-center w-full px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                                  onClick={() => setActiveActionId(null)}
                                >
                                  <Edit className="w-4 h-4 mr-2" /> Edit Hymn
                                </Link>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteHymn(hymn.id);
                                    setActiveActionId(null);
                                  }}
                                  className="flex items-center w-full px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors border-t border-gray-700/50 mt-1"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Body */}
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2 line-clamp-1 group-hover:text-blue-400 transition-colors">
                          {hymn.title}
                        </h3>
                        <p className="text-sm text-gray-400 mb-4 flex items-center">
                          {hymn.author ? (
                            <>
                              by{" "}
                              <span className="text-gray-300 ml-1 font-medium">
                                {hymn.author.name}
                              </span>
                            </>
                          ) : (
                            <span className="italic">Unknown Author</span>
                          )}
                        </p>
                        <p className="text-gray-500 text-sm line-clamp-2 leading-relaxed h-10 mb-4">
                          {hymn.summary ||
                            "No description available for this hymn."}
                        </p>
                      </div>

                      {/* Footer Stats / Actions */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                        <button className="text-gray-600 hover:text-red-500 transition-colors flex items-center space-x-1 text-xs">
                          <Heart className="w-4 h-4" />
                          <span>Favorite</span>
                        </button>

                        <Link
                          to={`/d/hymns/${hymn.id}/edit`}
                          className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          EDIT →
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Pagination Component */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  color="blue"
                  radius="md"
                  styles={(theme) => ({
                    control: {
                      backgroundColor: "transparent",
                      border: `1px solid ${theme.colors.gray[8]}`,
                      color: theme.colors.gray[4],
                      "&:hover": {
                        backgroundColor: theme.colors.gray[8],
                        color: theme.white,
                      },
                      "&[data-active]": {
                        backgroundImage: theme.fn.gradient({
                          from: "blue",
                          to: "cyan",
                        }),
                        border: 0,
                        color: theme.white,
                      },
                    },
                  })}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HymnsPage;
